import styled from '@emotion/styled';
import { HTMLAttributes, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import KnowledgeTypeRenderer from './KnowledgeTypeRenderer';
import ConditionRenderer from './ConditionRenderer';
import { useGetKnowledgeText } from '@knowledge/knowledges/general/KnowledgeListForm/KnowledgeType';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { UnConfigStore } from 'views/BootGuide/views/AiModel/share';
import { useField } from 'formik';

export interface RuleProps<T extends Knowledge.KnowledgeType>
  extends HTMLAttributes<HTMLDivElement> {
  knowledgeType: T;
  index: number;
  knowledgeValue: Knowledge.Value.Value<T>;
  stores?: UnConfigStore[];
  storeMap: Partial<Record<number, UnConfigStore>>;
  onEdit: (knowledgeValue: Knowledge.Value.Value<T>) => void;
}

function Rule<T extends Knowledge.KnowledgeType>(props: RuleProps<T>) {
  const { index, knowledgeType, knowledgeValue, stores, storeMap, onEdit, ...otherProps } = props;
  const language = useSystemLanguageValue();

  const { getKnowledgeText } = useGetKnowledgeText();

  const { t } = useTranslate();

  const [{ value }] = useField(`kc.${index}.adaptValue`);

  const label = useMemo(() => {
    return language !== 'zh-CN' ? `${t('knowledge.下列情况')}:` : t('knowledge.下列情况');
  }, [language, t]);

  return (
    <Container {...otherProps}>
      <header>
        <h4>
          {label}
          {getKnowledgeText(knowledgeType, value as any) ?? ''}
        </h4>
      </header>
      <section>
        <div className="max-h-[400px] overflow-auto">
          <ConditionRenderer values={knowledgeValue.conditions} stores={stores} onEdit={onEdit} />
        </div>
        <div>
          <KnowledgeTypeRenderer
            singleMode
            fieldNamePrefix={`kc.${index}`}
            type={knowledgeType}
            storeMap={storeMap}
          />
        </div>
      </section>
    </Container>
  );
}

const Container = styled.div`
  border: 1px solid var(--fett-border-strong);
  background: var(--fett-bg-panel);
  border-radius: 2px;
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 48px;
    padding: 0 16px;
    background: var(--fett-erp-card-bg);
    > h4 {
      flex: 1 0 0;
      margin-bottom: 0;
    }
  }
  > section {
    display: flex;
    margin: 16px;
    > div {
      flex: 1 0 0;
      padding: 16px;
      border: 1px solid var(--fett-border-default);
      border-radius: 2px;
      & + div {
        flex: 0 0 550px;
        margin-left: 16px;
      }
    }
  }
`;

export default Rule;
